import {computeHasRealSelection, computeSelectionBox} from 'notebook/tables/store/logic/TableSelectionLogic';
import {createTableModelSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore, TableEditableVolatileProps} from 'notebook/tables/store/TableStore';
import {MultiSelectionBox} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';

export interface TableSelectionModel {
  /**
   * The normalized selection box with min/max bounds.
   * Returns null if there is no selection.
   */
  selectionBox: MultiSelectionBox | null;

  /**
   * Whether there is a real (valid) selection within the table bounds.
   */
  hasRealSelection: boolean;

  /**
   * Whether there is any multi-selection (both start and end indices are non-null).
   */
  hasMultiSelection: boolean;

  /**
   * The multi-select key for the table, used for multi-selection tracking.
   */
  multiSelectKey: string;

  /*
  TODO(BNCH-146929): Future properties to be added as logic is transitioned out of TableEditable:
  expandedBox: MultiSelectionBox | null; // From _multiSelectionBox()
  multiAxisDimSelection: MultiAxisDimSelection; // From _multiAxisDimSelection()
  selectionRange: SelectionRange | null; // From getSelectionRange()
  */
}

function createSelectionBoxSelector(): (store: TableEditableStore) => MultiSelectionBox | null {
  return createTableModelSelector(
    [(store) => store.state.multiStartIndex, (store) => store.state.multiEndIndex],
    (multiStartIndex, multiEndIndex) => computeSelectionBox(multiStartIndex, multiEndIndex)
  );
}

export function createTableSelectionSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps
): (store: TableEditableStore) => TableSelectionModel {
  return createTableModelSelector(
    [
      createSelectionBoxSelector(),
      (store) => volatilePropsSelector(store).table,
      (store) => store.state.multiStartIndex,
      (store) => store.state.multiEndIndex,
      (store) => store.stableProps.cid,
    ],
    (selectionBox, table, multiStartIndex, multiEndIndex, cid) => ({
      selectionBox,
      hasRealSelection: computeHasRealSelection(selectionBox, table),
      hasMultiSelection: multiStartIndex != null && multiEndIndex != null,
      multiSelectKey: TableUtil.getTableMultiSelectKey(cid),
    })
  );
}
